/**
 * Lab Aggregate Accounts — map Aggregate-API search rows → console Account.
 *
 * Old Lab Take/Give (aggregate-accounts.html):
 *   balanceUp < 0 → Take |balanceUp|
 *   balanceUp > 0 → Give  balanceUp
 */

import { levelToRole } from '@/api/map/agent'
import { paiseToDisplay, parseCurrencyType } from '@/lib/currency'
import { unstopped, type Account } from '@/data/types'
import { STOP_COPY } from '@/lib/status'

export type AggregateAccountApiRow = {
  id: string
  username: string
  name: string
  level: number
  accessLevel?: string
  creditLimit: string
  /** Lab Member.balanceUp (minor). Prefer this over postedPnl. */
  balanceUp?: string
  postedPnl?: string
  /** Lab Available Credit / playerBalance (minor). */
  availableCredit?: string
  status: string
  currencyType?: string
  siteUrl?: string
  createdAt?: string
}

export type AggregateAccount = Account & {
  availableCredit: number
  balanceUp: number
  accessLevelLabel: string
}

export type AggregateAccountsSearchResult = {
  rows: AggregateAccount[]
}

/**
 * Lab Aggregate Accounts Take/Give from `balanceUp` in display units.
 */
export function takeGiveFromBalanceUp(balanceUp: number): { take: number; give: number } {
  if (balanceUp < 0) return { take: -balanceUp, give: 0 }
  if (balanceUp > 0) return { take: 0, give: balanceUp }
  return { take: 0, give: 0 }
}

/** Parent-POV settlement (+Take) → Lab Take/Give columns. */
export function takeGiveFromSettlement(settlement: number): { take: number; give: number } {
  return takeGiveFromBalanceUp(-settlement)
}

export function mapAggregateAccountRow(row: AggregateAccountApiRow): AggregateAccount {
  const currencyType = parseCurrencyType(row.currencyType)
  const balanceUpMinor = row.balanceUp ?? row.postedPnl ?? '0'
  const balanceUp = paiseToDisplay(balanceUpMinor, currencyType)
  const creditLimit = paiseToDisplay(row.creditLimit, currencyType)
  const availableCredit = paiseToDisplay(row.availableCredit ?? '0', currencyType)
  const role = levelToRole(row.level)
  const status = (row.status as Account['status']) ?? 'active'
  const username = row.username?.trim() || row.name

  return {
    id: row.id,
    username,
    displayName: row.name || username,
    role,
    parentId: null,
    path: [],
    status,
    createdAt: row.createdAt ?? '',
    lastActiveAt: null,
    currencyType,
    creditLimit,
    balance: 0,
    distributedBalance: 0,
    exposure: 0,
    // Parent-POV: positive Take (Lab balanceUp negated)
    settlement: balanceUp === 0 ? 0 : -balanceUp,
    share: { sportsbook: 0, casino: 0 },
    commission: { fancy: 0, bookmaker: 0, matchOdds: 0 },
    betLimits: { min: 0, max: 0 },
    games: { casino: true, cardGame: true, liveGame: true, sportsbook: true },
    childCount: 0,
    memberCount: 0,
    flags: [],
    ...unstopped(status, STOP_COPY.notWiredLive),
    availableCredit,
    balanceUp,
    accessLevelLabel: row.accessLevel ?? '',
  }
}

export function mapAggregateAccountsSearch(
  rows: AggregateAccountApiRow[],
): AggregateAccountsSearchResult {
  return { rows: rows.map(mapAggregateAccountRow) }
}

/** Lab: non-Player rows can drill into downline. */
export function canDrillAggregateAccount(row: Pick<AggregateAccount, 'role'>): boolean {
  return row.role !== 'player'
}

/**
 * Lab aggregate-accounts.html column gates on the *viewer* accessLevel:
 *   Currency → CompanyMaster only
 *   Available Credit → Master only
 * Aggregate login sees neither (Access Level column is commented out in Lab).
 */
export function labAggregateAccountsColumns(viewerAccessLevel: string | undefined | null): {
  showCurrency: boolean
  showAvailableCredit: boolean
} {
  const level = String(viewerAccessLevel ?? '').trim()
  return {
    showCurrency: level === 'CompanyMaster',
    showAvailableCredit: level === 'Master',
  }
}

/**
 * Lab aggregate-accounts.html: `{{ item.createdAt | date: "d MMM yyyy HH:mm:ss" }}`
 * (local time). e.g. `1 Feb 2026 15:30:00`.
 */
export function formatLabAggregateCreatedAt(
  value: Date | string | number | null | undefined,
): string {
  if (value == null || value === '') return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  const mon = d.toLocaleString('en-GB', { month: 'short' })
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getDate()} ${mon} ${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}
